// PredicaoBoletoValorCard.jsx
import React from "react";
import { Card, Badge, Alert, Tooltip } from "flowbite-react";
import { HiInformationCircle } from "react-icons/hi";
import { format, startOfMonth, addMonths } from "date-fns";
import { ptBR } from "date-fns/locale";
import TitleStripe from "../TitleStripe";

/**
 * Props:
 * - data: {
 *     indicador: string | number,   // valor previsto para o próximo mês
 *     segmentacao?: string,         // opcional
 *     providerId?: number,          // opcional
 *     atualizadoEm?: string|Date,   // opcional
 *   }
 * - baseDate?: Date                 // referência (default: hoje)
 */

const brl = (v) =>
  (Number(v) || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

const labelMesLongo = (d) => format(d, "MMMM 'de' yyyy", { locale: ptBR });
const labelMesCurto = (d) => format(d, "MMM/yy", { locale: ptBR });

export default function PredicaoRecebimentoBoletoPJQuery({ data, baseDate }) {
  const ref = baseDate ? new Date(baseDate) : new Date();
  const proximoMes = addMonths(startOfMonth(ref), 1);

  // Estado vazio quando não há indicador
  if (data?.indicador == null) {
    return (
      <div>
        <TitleStripe>Recebimentos previstos via boleto</TitleStripe>
        <Card className="mt-4 bg-gray-50 border border-dashed">
          <Alert color="info" icon={HiInformationCircle}>
            <span className="font-medium">Sem dados para previsão.</span> Não há
            movimentação suficiente ou não encontramos informações desta empresa
            para estimar o próximo mês.
          </Alert>
        </Card>
      </div>
    );
  }

  const previsto = Number(String(data.indicador).replace(",", ".")) || 0;
  const atualizadoEmStr = data?.atualizadoEm
    ? format(new Date(data.atualizadoEm), "dd/MM/yyyy HH:mm", { locale: ptBR })
    : null;

  return (
    <div>
      <TitleStripe>Recebimentos previstos via boleto</TitleStripe>

      <div className="relative mt-4 overflow-hidden rounded-2xl border border-gray-200 bg-gray-50 p-0 shadow-sm dark:border-gray-700">
        {/* Conteúdo */}
        <div className="relative grid gap-0 grid-cols-3">
          {/* Cabeçalho e selo */}
          <div className="col-span-1 p-6 border-b border-b-0 border-r border-gray-200/70 dark:border-gray-700/60 bg-white/70 dark:bg-gray-900/40 backdrop-blur">
            <div className="flex items-center justify-between">
              <Badge color="success" className="uppercase">
                Próximo mês
              </Badge>
              <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                {labelMesCurto(proximoMes)}
              </span>
            </div>
            <h3 className="mt-2 text-lg font-bold tracking-tight text-gray-900 dark:text-white">
              Recebimentos previstos via boleto
            </h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              Estimativa de entradas financeiras com base em comportamento
              recente.
            </p>
          </div>

          {/* Valor grande */}
          <div className="col-span-2 p-6">
            <div className="text-sm font-semibold text-gray-600 dark:text-gray-300">
              Valor previsto — {labelMesLongo(proximoMes)}
            </div>

            <div className="mt-2 text-4xl font-bold tracking-tight text-green-700 dark:text-white">
              {brl(previsto)}
            </div>

            <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
              Esta é uma **estimativa** e pode variar conforme sazonalidade,
              inadimplência e políticas de cobrança. Combine com pipeline e taxa
              de conversão recentes para maior precisão.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
